import React, { useEffect, useState } from 'react'
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    TextField,
    Typography,
} from '@material-ui/core'
import { Send } from '@material-ui/icons'

import { RenewalSequence } from '../../models/Concession'

interface SendOffersDialogProps {
    open: boolean
    offerCount: number
    tenantName?: string
    sequences: RenewalSequence[]
    onConfirm: (sequence: number | null) => void
    onClose: () => void
    isLoading?: boolean
}

const NO_SEQUENCE_VALUE = -1

export const SendOffersDialog: React.FC<SendOffersDialogProps> = ({
    open,
    offerCount,
    tenantName,
    sequences,
    onConfirm,
    onClose,
    isLoading,
}) => {
    const [selectedSequence, setSelectedSequence] = useState<number>(
        NO_SEQUENCE_VALUE,
    )

    useEffect(() => {
        if (open) {
            const active = sequences.find((s) => s.is_active)
            setSelectedSequence(active ? active.id : NO_SEQUENCE_VALUE)
        }
    }, [open, sequences])

    const handleConfirm = () => {
        onConfirm(
            selectedSequence === NO_SEQUENCE_VALUE ? null : selectedSequence,
        )
    }

    const isBulk = offerCount > 1
    const confirmText = isBulk ? `Send ${offerCount} offers` : 'Send offer'
    const bodyText = isBulk
        ? `Send ${offerCount} draft offer${
              offerCount === 1 ? '' : 's'
          }? They will become visible to tenants.`
        : `Send the renewal offer to ${
              tenantName ?? 'this tenant'
          }? It will become visible to the tenant.`

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{ style: { borderRadius: 12, padding: 8 } }}
        >
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                pt={3}
                pb={1}
            >
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    style={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        backgroundColor: '#6570E615',
                    }}
                >
                    <Send style={{ color: '#6570E6' }} />
                </Box>
            </Box>

            <DialogTitle
                disableTypography
                style={{ textAlign: 'center', paddingBottom: 0 }}
            >
                <Typography
                    variant="h6"
                    style={{ fontWeight: 600, fontSize: '1.1rem' }}
                >
                    {isBulk ? 'Send offers' : 'Send offer'}
                </Typography>
            </DialogTitle>

            <DialogContent>
                <Typography
                    variant="body2"
                    color="textSecondary"
                    style={{ textAlign: 'center', lineHeight: 1.6 }}
                >
                    {bodyText}
                </Typography>

                <Box mt={2.5}>
                    <TextField
                        select
                        label="Messaging sequence"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={selectedSequence}
                        onChange={(e) =>
                            setSelectedSequence(Number(e.target.value))
                        }
                    >
                        <MenuItem value={NO_SEQUENCE_VALUE}>
                            <em>No automated messaging</em>
                        </MenuItem>
                        {sequences.map((s) => (
                            <MenuItem key={s.id} value={s.id}>
                                {s.name}
                                {s.is_active ? ' (active)' : ''}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Typography
                        variant="caption"
                        color="textSecondary"
                        style={{ marginTop: 4, display: 'block' }}
                    >
                        Choose a sequence to automatically send messages after
                        the offer is delivered.
                    </Typography>
                </Box>
            </DialogContent>

            <DialogActions style={{ padding: '8px 24px 20px', gap: 8 }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    fullWidth
                    disabled={isLoading}
                    style={{
                        textTransform: 'none',
                        borderRadius: 8,
                        fontWeight: 600,
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    fullWidth
                    onClick={handleConfirm}
                    disabled={isLoading}
                    style={{
                        textTransform: 'none',
                        borderRadius: 8,
                        fontWeight: 600,
                        color: '#fff',
                        backgroundColor: '#6570E6',
                        boxShadow: 'none',
                    }}
                >
                    {isLoading ? (
                        <CircularProgress size={20} style={{ color: '#fff' }} />
                    ) : (
                        confirmText
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
